import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Info, AlertTriangle, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/constants";

export default function MisEspacios() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [enteringIdx, setEnteringIdx] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("mis_espacios");
      if (raw) setAnalysis(JSON.parse(raw));
    } catch (e) { console.error("MisEspacios: no se pudo parsear sessionStorage", e); }
  }, []);

  const enterGroup = async (group, idx) => {
    setEnteringIdx(idx);
    try {
      const { data } = await api.post("/expositor/spaces/enter", { stand_numbers: group.codes });
      toast.success(group.combined ? `Espacios unidos · ${group.codes.join(" + ")}` : `Ingresando a ${data.primary_stand}`);
      navigate(`/stand/${data.primary_stand}`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo ingresar al espacio");
    } finally { setEnteringIdx(null); }
  };

  const fmtPrice = (n) => n == null ? "—" : `USD ${Number(n).toLocaleString("es-AR")}`;

  if (!analysis) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex flex-col items-center justify-center gap-4 px-6">
        <div className="text-sm text-zinc-400 text-center max-w-md">
          No hay espacios cargados. Volvé al ingreso y cargá tus stands.
        </div>
        <Button onClick={() => navigate("/")} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al ingreso
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] bg-grain text-white">
      <header className="border-b border-zinc-800/60 backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={BRAND.LOGO_AM} alt="AM Norte 2026" className="h-9 w-auto" />
          <Button
            data-testid="mis-espacios-back-btn"
            onClick={() => navigate("/")}
            variant="outline"
            className="border-zinc-800 hover:bg-zinc-900 rounded-sm h-8 px-3 text-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Volver al ingreso
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF5A00]">Mis espacios</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-2">
            {analysis.groups.length} configuración{analysis.groups.length === 1 ? "" : "es"} detectada{analysis.groups.length === 1 ? "" : "s"}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            Stands cargados: <span className="font-mono text-white">{analysis.input_codes.join(", ")}</span>
          </p>
        </div>

        {analysis.has_multiple_groups && (
          <div data-testid="mis-espacios-info" className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 text-xs text-amber-200 flex gap-2">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{analysis.info_message}</span>
          </div>
        )}

        <div className="space-y-3">
          {analysis.groups.map((g, i) => (
            <div
              key={g.codes.join("+")}
              data-testid={`mis-espacios-group-${i}`}
              className={`border rounded-sm p-5 space-y-3 ${g.requires_manual_quote ? "border-amber-500/50 bg-amber-500/5" : "border-zinc-800 bg-zinc-950"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Grupo {i + 1} · {g.combined ? "Contiguos (unidos)" : g.codes.length > 1 ? "Contiguos" : "Independiente"}
                  </div>
                  <div className="text-2xl font-mono font-bold text-white mt-1">
                    {g.codes.join(" + ")}
                  </div>
                </div>
                {g.requires_manual_quote && (
                  <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-sm flex items-center gap-1 shrink-0">
                    <AlertTriangle className="w-3 h-3" /> A cotizar
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs text-zinc-300 border-y border-zinc-800/70 py-3">
                <div><div className="text-zinc-500">Superficie</div><div className="text-white">{g.superficie} m²</div></div>
                <div><div className="text-zinc-500">Tipología</div><div className="text-white">{g.tipo_label}</div></div>
                <div><div className="text-zinc-500">Paneles</div><div className="text-white">{g.n_paneles || "A definir"}</div></div>
                <div>
                  <div className="text-zinc-500">Precio</div>
                  <div>
                    {g.requires_manual_quote
                      ? <span className="text-amber-300 font-medium">A cotizar</span>
                      : <span className="text-[#FF5A00] font-mono font-medium">{fmtPrice(g.price)} + IVA</span>}
                  </div>
                </div>
              </div>
              {!g.requires_manual_quote && g.extra_m2 > 0 && (
                <div className="text-[10px] text-zinc-500">{g.formula}</div>
              )}
              {g.requires_manual_quote && (
                <div className="text-[11px] text-amber-200/80 leading-relaxed">
                  Grupo Península / 1 pared. El precio queda pendiente de cotización manual por Servicios Oficiales. Podés continuar configurando paneles, gráficas, mobiliario y upgrades; administración recibirá un aviso automático.
                </div>
              )}
              <Button
                type="button"
                data-testid={`mis-espacios-enter-${i}`}
                onClick={() => enterGroup(g, i)}
                disabled={enteringIdx === i}
                className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-10 w-full text-xs"
              >
                {enteringIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Ingresar a configurar <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          ))}
        </div>

        {analysis.groups.length > 1 && (
          <div className="text-[11px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-4">
            Cada grupo mantiene su propia configuración, paneles, gráficas, upgrades, estado y correos.
            Los datos generales (empresa, contacto, facturación) pueden reutilizarse entre grupos.
            Podés enviar un grupo sin haber terminado el otro.
          </div>
        )}
      </main>
    </div>
  );
}
